import { useEffect, useRef, type PointerEventHandler } from "react";
import { projects } from "../../data/projects";
import { sectionEntryConfigs } from "../../lib/sectionEntry";
import { getNearestWrappedProgress } from "../../lib/projectsCarousel";
import { useAppStore } from "../../store/useAppStore";
import { useProjectsCarouselStore } from "../../store/useProjectsCarouselStore";
import { useSectionEntryStore } from "../../store/useSectionEntryStore";
import { ProjectMediaPanel } from "./projects/ProjectMediaPanel";
import { ProjectsCounter } from "./projects/ProjectsCounter";
import { ProjectsInfoPanel } from "./projects/ProjectsInfoPanel";
import { ProjectsNav } from "./projects/ProjectsNav";

const totalProjects = projects.length;
const projectsInitialIndex = sectionEntryConfigs.projects?.initialStateIndex ?? 0;

export function ProjectsSection() {
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const settleTweenRef = useRef<{ kill: () => void } | null>(null);
  const rotationProgressRef = useRef(0);
  const activeIndexRef = useRef(0);
  const dragStateRef = useRef<{
    pointerId: number | null;
    startX: number;
    startProgress: number;
    hasMoved: boolean;
  }>({
    pointerId: null,
    startX: 0,
    startProgress: 0,
    hasMoved: false
  });

  const reducedMotion = useAppStore((state) => state.reducedMotion);
  const currentSection = useAppStore((state) => state.currentSection);
  const activeIndex = useProjectsCarouselStore((state) => state.activeIndex);
  const direction = useProjectsCarouselStore((state) => state.direction);
  const isAnimating = useProjectsCarouselStore((state) => state.isAnimating);
  const isDragging = useProjectsCarouselStore((state) => state.isDragging);
  const rotationProgress = useProjectsCarouselStore((state) => state.rotationProgress);
  const projectsEntryState = useSectionEntryStore((state) => state.sections.projects);
  const setActiveIndex = useProjectsCarouselStore((state) => state.setActiveIndex);
  const setAnimating = useProjectsCarouselStore((state) => state.setAnimating);
  const setDirection = useProjectsCarouselStore((state) => state.setDirection);
  const setDragging = useProjectsCarouselStore((state) => state.setDragging);
  const setRotationProgress = useProjectsCarouselStore((state) => state.setRotationProgress);

  const activeProject = projects[activeIndex] ?? projects[0];

  useEffect(() => {
    rotationProgressRef.current = rotationProgress;
  }, [rotationProgress]);

  useEffect(() => {
    activeIndexRef.current = activeIndex;
  }, [activeIndex]);

  useEffect(() => {
    if (currentSection !== "projects" || !projectsEntryState.isEntryLocked) {
      return;
    }

    stopSettling();
    rotationProgressRef.current = projectsInitialIndex;
    setRotationProgress(projectsInitialIndex);
    setActiveIndex(projectsInitialIndex);
    setDirection(0);
  }, [currentSection, projectsEntryState.isEntryLocked, setActiveIndex, setDirection, setRotationProgress]);

  useEffect(() => {
    if (reducedMotion || !sectionRef.current || isDragging) {
      return;
    }

    let active = true;
    let cleanup: (() => void) | undefined;

    void import("gsap").then(({ gsap }) => {
      if (!active || !sectionRef.current) {
        return;
      }

      const context = gsap.context(() => {
        gsap.set("[data-project-animate='card']", {
          clearProps: "transform,opacity"
        });
      }, sectionRef);

      cleanup = () => context.revert();
    });

    return () => {
      active = false;
      cleanup?.();
    };
  }, [activeIndex, isDragging, reducedMotion]);

  useEffect(() => {
    return () => {
      settleTweenRef.current?.kill();
    };
  }, []);

  const stopSettling = () => {
    settleTweenRef.current?.kill();
    settleTweenRef.current = null;
    setAnimating(false);
  };

  const snapToProgress = async (nextProgress: number) => {
    if (Math.abs(rotationProgressRef.current - nextProgress) < 0.001) {
      setDirection(0);
      setAnimating(false);
      return;
    }

    stopSettling();
    setDirection(nextProgress > rotationProgressRef.current ? 1 : -1);
    setAnimating(true);

    const { gsap } = await import("gsap");
    const motionState = { progress: rotationProgressRef.current };

    settleTweenRef.current = gsap.to(motionState, {
      duration: reducedMotion ? 0.28 : 0.8,
      ease: reducedMotion ? "power1.out" : "power3.out",
      overwrite: true,
      progress: nextProgress,
      onUpdate: () => {
        rotationProgressRef.current = motionState.progress;
        setRotationProgress(motionState.progress);
      },
      onInterrupt: () => {
        setAnimating(false);
      },
      onComplete: () => {
        rotationProgressRef.current = nextProgress;
        setRotationProgress(nextProgress);
        setActiveIndex(Math.round(nextProgress));
        setDirection(0);
        setAnimating(false);
        settleTweenRef.current = null;
      }
    });
  };

  const snapToIndex = (nextIndex: number) => {
    const nextProgress = getNearestWrappedProgress(nextIndex, rotationProgressRef.current, totalProjects);
    return snapToProgress(nextProgress);
  };

  const snapByStep = (step: number) => {
    const nextProgress = Math.round(rotationProgressRef.current) + step;
    return snapToProgress(nextProgress);
  };

  const handlePointerDown: PointerEventHandler<HTMLDivElement> = (event) => {
    const target = event.target as HTMLElement | null;
    if (projectsEntryState.isEntryLocked || target?.closest("[data-carousel-control='true']")) {
      return;
    }

    if (isAnimating) {
      stopSettling();
    }

    dragStateRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startProgress: rotationProgressRef.current,
      hasMoved: false
    };

    event.currentTarget.setPointerCapture(event.pointerId);
    setDragging(true);
  };

  const handlePointerMove: PointerEventHandler<HTMLDivElement> = (event) => {
    if (dragStateRef.current.pointerId !== event.pointerId) {
      return;
    }

    const bounds = event.currentTarget.getBoundingClientRect();
    const deltaX = event.clientX - dragStateRef.current.startX;
    if (Math.abs(deltaX) > 8) {
      dragStateRef.current.hasMoved = true;
    }
    const progressDelta = bounds.width > 0 ? deltaX / (bounds.width * 0.58) : 0;
    const nextProgress = dragStateRef.current.startProgress - progressDelta;
    const nextDirection = nextProgress > rotationProgressRef.current ? 1 : nextProgress < rotationProgressRef.current ? -1 : 0;

    rotationProgressRef.current = nextProgress;
    setDirection(nextDirection);
    setRotationProgress(nextProgress);
  };

  const handlePointerUp: PointerEventHandler<HTMLDivElement> = (event) => {
    if (dragStateRef.current.pointerId !== event.pointerId) {
      return;
    }

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    dragStateRef.current.pointerId = null;
    setDragging(false);

    if (!dragStateRef.current.hasMoved) {
      const bounds = event.currentTarget.getBoundingClientRect();
      const relativeX = event.clientX - bounds.left;
      const normalizedX = bounds.width > 0 ? relativeX / bounds.width : 0.5;

      if (normalizedX < 0.34) {
        void snapByStep(-1);
        return;
      }

      if (normalizedX > 0.66) {
        void snapByStep(1);
        return;
      }
    }

    void snapToProgress(Math.round(rotationProgressRef.current));
  };

  const handlePointerCancel: PointerEventHandler<HTMLDivElement> = (event) => {
    if (dragStateRef.current.pointerId !== event.pointerId) {
      return;
    }

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    dragStateRef.current.pointerId = null;
    setDragging(false);
    void snapToIndex(activeIndexRef.current);
  };

  return (
    <section className="experience-section experience-section--projects">
      <div className="projects-sectionInner" ref={sectionRef}>
        <div className="projects-layout">
          <div className="projects-infoWrap">
            <div className="projects-kicker projects-kicker--desktop">
              <div className="projects-kicker__label">Selected Work</div>
              <ProjectsCounter className="projects-kicker__counter" current={activeIndex} total={totalProjects} />
            </div>
            <ProjectsInfoPanel current={activeIndex} project={activeProject} total={totalProjects} />
            <div className="projects-infoWrap__mobileNav" data-carousel-control="true">
              <ProjectsNav
                activeIndex={activeIndex}
                disabled={projectsEntryState.isEntryLocked || isDragging || isAnimating}
                onNext={() => void snapByStep(1)}
                onPrev={() => void snapByStep(-1)}
                onSelect={(index) => void snapToIndex(index)}
                total={totalProjects}
              />
            </div>
          </div>

          <ProjectMediaPanel
            className={isDragging ? "projects-mediaPanel--dragging" : "projects-mediaPanel--idle"}
            label={
              <div className="projects-kicker projects-kicker--mobile">
                <div className="projects-kicker__label">Selected Work</div>
                <ProjectsCounter className="projects-kicker__counter" current={activeIndex} total={totalProjects} />
              </div>
            }
            nav={
              <ProjectsNav
                activeIndex={activeIndex}
                disabled={projectsEntryState.isEntryLocked || isDragging || isAnimating}
                onNext={() => void snapByStep(1)}
                onPrev={() => void snapByStep(-1)}
                onSelect={(index) => void snapToIndex(index)}
                total={totalProjects}
              />
            }
            onPointerCancel={handlePointerCancel}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
          />
        </div>
      </div>
    </section>
  );
}
